import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";

function fmt(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
}
const keyOf = (r) => (r.kind === "page" ? r.slug : r.id);

export function TrashSection({ toolkit, refreshKey, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/trash?toolkit=${toolkit}`); setItems(data); }
    catch { setItems([]); }
  }, [toolkit]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const restore = async (r) => {
    try {
      await api.post(`/admin/items/${r.kind}/${keyOf(r)}/restore`);
      toast.success("Restored.");
      load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore."); }
  };
  const purge = async () => {
    const r = confirm; setConfirm(null);
    try {
      await api.delete(`/admin/items/${r.kind}/${keyOf(r)}/permanent`);
      toast.success("Permanently deleted.");
      load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };

  const count = items?.length ?? 0;
  return (
    <section className="mt-10" data-testid="trash-section">
      <button data-testid="trash-toggle" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15">
        <Trash2 className="h-4 w-4 text-sbg-raspberry" /> Trash ({count}) {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="mt-4 overflow-hidden rounded-2xl bg-white ring-1 ring-sbg-raspberry/10">
          {items === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sbg-raspberry" /></div>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-sbg-burgundy/60" data-testid="trash-empty">Trash is empty.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-sbg-blush/40 text-left text-xs font-bold uppercase tracking-wider text-sbg-raspberry">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 hidden sm:table-cell">Deleted</th><th className="px-4 py-3">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-sbg-raspberry/10">
                {items.map((r) => (
                  <tr key={`${r.kind}-${keyOf(r)}`} data-testid={`trash-row-${keyOf(r)}`}>
                    <td className="px-4 py-3 font-semibold text-sbg-burgundy">{r.name || r.email || "—"}</td>
                    <td className="px-4 py-3 text-sbg-burgundy/70">{r.kind === "page" ? "Page" : "Frame only"}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-xs text-sbg-burgundy/60">{fmt(r.deleted_at)}<br />by {r.deleted_by}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button data-testid={`trash-restore-${keyOf(r)}`} onClick={() => restore(r)} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-sbg-turquoise px-3 py-1.5 text-xs font-bold text-sbg-burgundy"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                        {user?.is_admin && (
                          <button data-testid={`trash-purge-${keyOf(r)}`} onClick={() => setConfirm(r)} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-sbg-burgundy px-3 py-1.5 text-xs font-bold text-white"><Trash2 className="h-3.5 w-3.5" /> Delete forever</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!user?.is_admin && <p className="px-4 pb-4 text-xs text-sbg-burgundy/50">Only an Admin can permanently delete items.</p>}
        </div>
      )}
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} confirmLabel="Delete forever" onConfirm={purge}
        title={`Permanently delete ${confirm?.name || confirm?.email || "this item"}?`}
        description="This removes the entry and its photos from the database for good. This cannot be undone." />
    </section>
  );
}
